/**
 * L'état d'interface de « Paramètres », pur, pour l'élève comme pour le tuteur : la page ouverte (la
 * liste et ses pages vivent dans la même route, `settings`), ce que la personne a modifié sur chacune
 * avant d'enregistrer, les erreurs de la page ouverte, l'accusé, et la garde « Modifications non
 * enregistrées ». Chaque espace n'y met que ses pages (`S`) et leurs brouillons (`D`).
 *
 * Une page n'a de brouillon qu'à partir de la première modification : sans brouillon, elle montre
 * les valeurs du compte (le view-model). Un enregistrement réussi efface le brouillon — le compte
 * renvoyé par le serveur porte désormais ces valeurs.
 */
import { type ApiNotificationSettings } from "../../adapters/student/Account/studentSettingsAdapter"

/** Les brouillons d'un espace : un par page, `null` tant que rien n'a bougé ; les notifications toujours. */
export type SettingsDrafts<S extends string> = Record<S, unknown> & {
    /** Les notifications basculées (`user.notification_settings` recomposé). */
    preferences: ApiNotificationSettings | null
}

export type SettingsUiState<S extends string, D extends SettingsDrafts<S>> = {
    view: "hub" | S
    /** La page a été ouverte directement (`initialTab`) : son retour quitte « Paramètres ». */
    enteredDirectly: boolean
    drafts: D
    /** Les erreurs de champ de la page ouverte. */
    errors: Record<string, string>
    submitError: string | null
    savedMessage: string | null
    /** La garde est ouverte : où aller si la personne confirme. */
    pendingLeave: "hub" | "exit" | null
}

export type SettingsUiAction<S extends string> =
    | { type: "OPEN"; view: S }
    | { type: "EDIT"; section: S; value: object; field: string }
    | { type: "SET_PREFERENCES"; value: ApiNotificationSettings }
    | { type: "INVALID"; errors: Record<string, string>; submitError: string | null }
    | { type: "SUBMITTING" }
    | { type: "SAVED"; section: S; message: string }
    | { type: "DISMISS_SAVED" }
    | { type: "ASK_LEAVE"; target: "hub" | "exit" }
    | { type: "CANCEL_LEAVE" }
    | { type: "LEAVE_SECTION" }

/** `settings` reçoit l'index d'un onglet de l'ancien écran : `viewByTab` dit lequel ouvre une page. */
export function initialSettingsUiState<S extends string, D extends SettingsDrafts<S>>(
    viewByTab: Record<number, S>,
    emptyDrafts: D,
    initialTab?: unknown
): SettingsUiState<S, D> {
    const view = typeof initialTab === "number" ? viewByTab[initialTab] : undefined
    return {
        view: view ?? "hub",
        enteredDirectly: view != null,
        drafts: emptyDrafts,
        errors: {},
        submitError: null,
        savedMessage: null,
        pendingLeave: null,
    }
}

const sameValues = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b)

/** La page a des modifications non enregistrées : son brouillon diffère de ce que le compte porte. */
export function isSectionDirty(draft: unknown, base: unknown): boolean {
    return draft != null && !sameValues(draft, base)
}

/** La page ouverte a des modifications non enregistrées (`base` : les valeurs du compte, par page). */
export function settingsViewDirty<S extends string, D extends SettingsDrafts<S>>(
    ui: SettingsUiState<S, D>,
    base: Partial<Record<S, unknown>>
): boolean {
    if (ui.view === "hub") return false
    // Les notifications n'ont pas de valeurs à comparer : un basculement suffit.
    if (ui.view === "preferences") return ui.drafts.preferences != null
    return isSectionDirty(ui.drafts[ui.view], base[ui.view])
}

export function settingsUiReducer<S extends string, D extends SettingsDrafts<S>>(
    state: SettingsUiState<S, D>,
    action: SettingsUiAction<S>
): SettingsUiState<S, D> {
    switch (action.type) {
        case "OPEN":
            return { ...state, view: action.view, errors: {}, submitError: null }
        case "EDIT": {
            const errors = { ...state.errors }
            delete errors[action.field]
            return { ...state, drafts: { ...state.drafts, [action.section]: action.value }, errors }
        }
        case "SET_PREFERENCES":
            return { ...state, drafts: { ...state.drafts, preferences: action.value } }
        case "INVALID":
            return { ...state, errors: action.errors, submitError: action.submitError }
        case "SUBMITTING":
            return { ...state, errors: {}, submitError: null, savedMessage: null }
        case "SAVED":
            return { ...state, drafts: { ...state.drafts, [action.section]: null }, savedMessage: action.message }
        case "DISMISS_SAVED":
            return { ...state, savedMessage: null }
        case "ASK_LEAVE":
            return { ...state, pendingLeave: action.target }
        case "CANCEL_LEAVE":
            return { ...state, pendingLeave: null }
        case "LEAVE_SECTION": {
            // Quitter une page abandonne ce qui n'a pas été enregistré, comme l'ancien écran en partant.
            if (state.view === "hub") return { ...state, pendingLeave: null }
            return {
                ...state,
                view: "hub",
                drafts: { ...state.drafts, [state.view]: null },
                errors: {},
                submitError: null,
                pendingLeave: null,
            }
        }
        default:
            return state
    }
}
